<template>
  <div
    class="mlp-root"
    :class="{ expanded: isExpanded }"
    @mouseenter="localExpanded = true"
    @mouseleave="handleMouseLeave"
  >
    <section class="mlp-topcard d-flex flex-column">
      <img
        src="../../assets/NewDashboard/shadowpng.png"
        class="topcard-img-shadow"
        alt=""
      />
      <!-- Top row: avatar + arrow button -->
      <div
        class="topcard-header d-flex justify-content-between align-items-center"
      >
        <div class="avatar-wrapper">
          <svg class="progress-ring" width="40" height="40" viewBox="0 0 40 40">
            <circle class="ring-bg" cx="20" cy="20" r="17" />
            <circle
              class="ring-fill"
              cx="20"
              cy="20"
              r="17"
              :stroke-dasharray="`${2 * Math.PI * 17}`"
              :stroke-dashoffset="`${
                2 * Math.PI * 17 * (1 - profileCompletion / 100)
              }`"
            />
          </svg>
          <img class="avatar-img" :src="user.avatar" :alt="user.name" />
          <div class="completion-badge">{{ profileCompletion }}%</div>
        </div>

        <img
          class="pointer"
          v-if="isExpanded"
          src="../../assets/NewDashboard/roundedarw.svg"
          alt=""
          @click="goToResumeBuilder"
        />
      </div>

      <!-- User info -->
      <div v-if="isExpanded" class="topcard-info">
        <h2 class="user-name m-0">
          {{ user.name }}
        </h2>
        <p class="user-role w-100 text-truncate">
          {{ user.role }}
        </p>
      </div>
      <!-- <div class="company-badge-container pb-2">
				<div class="company-badge d-flex align-items-center">
					<img src="../../assets/NewDashboard/googleicon.svg" alt="" />
					Google
				</div>
			</div> -->
      <!-- Complete profile banner: only shown when profile completion < 70% -->

      <!-- Complete profile banner -->
      <div
        v-if="isExpanded && showCompleteProfile"
        class="complete-banner text-center"
      >
        Complete your profile
      </div>
    </section>
    <section class="sidebar-nav">
      <div
        v-for="(section, si) in sidebarSections"
        :key="'mlp-section-' + si"
        class="sidebar-group"
      >
        <div
          v-if="section.divider"
          class="sidebar-divider"
          :class="{ expanded: isExpanded }"
        ></div>
        <template v-for="item in section.items">
          <div
            v-if="!item.children"
            :key="'link-' + item.label"
            :class="[
              'mlp-sidebar-inactive font-inter fw-400 fs-15 d-flex align-items-center',
              {
                'mlp-sidebar-active': isActive(item),
                expanded: isExpanded,
                'mlp-logout': item.logout,
              },
            ]"
            :data-route="item.candidatePath || item.routerName"
            @click="handleNav(item)"
          >
            <img
              v-if="item.icon"
              :src="activeIcon(item) ? item.iconActive : item.icon"
              :alt="item.label"
              class="sidebar-icon"
            />

            <span v-if="isExpanded" class="mlp-label">{{ item.label }}</span>
            <span v-if="item.badge && isExpanded" class="sidebar-badge">{{
              item.badge
            }}</span>
          </div>

          <!-- Collapsible parent (Resume) -->
          <div
            v-else
            :key="'collapse-' + item.label"
            class="mlp-sidebar-collapse"
          >
            <div
              :class="[
                'mlp-sidebar-inactive font-inter fw-400 fs-15 d-flex align-items-center mlp-collapse-toggle',
                {
                  'mlp-sidebar-active': isActive(item),
                  expanded: isExpanded,
                },
              ]"
              @click="toggleCollapse(item)"
            >
              <img
                :src="isActive(item) ? item.iconActive : item.icon"
                :alt="item.label"
                class="sidebar-icon"
              />
              <span v-if="isExpanded" class="mlp-label">{{ item.label }}</span>
              <img
                v-if="isExpanded"
                :src="
                  isSubmenuVisible(item)
                    ? require('@/assets/sidebar/sidebarUpArrow.svg')
                    : require('@/assets/sidebar/sidebardownarrow.svg')
                "
                alt="toggle"
                class="mlp-caret"
              />
            </div>

            <!-- Sub-items (Create Resume / Fix my resume) -->
            <transition name="mlp-fade">
              <div
                v-if="isExpanded && isSubmenuVisible(item)"
                class="mlp-submenu"
                :class="{ 'mlp-submenu-inactive': !hasActiveChild(item) }"
              >
                <div
                  v-for="child in item.children"
                  :key="child.label"
                  :class="[
                    'mlp-submenu-item font-inter fw-400 fs-15 d-flex align-items-center gap-2',
                    { 'mlp-sidebar-active': isActive(child) },
                  ]"
                  @click="handleChildNav(item, child)"
                >
                  <img
                    :src="activeIcon(child) ? child.iconActive : child.icon"
                    :alt="child.label"
                    class="sidebar-icon submenu-icon"
                  />
                  <span>{{ child.label }}</span>
                </div>
              </div>
            </transition>
          </div>
        </template>
      </div>
    </section>
    <LogoutConfirmModal
      :visible="showLogoutModal"
      @confirm="confirmLogout"
      @cancel="cancelLogout"
    />
    <ResumeClinicModal
      v-if="showResumeClinicModal"
      :showModal="showResumeClinicModal"
      @modalClose="closeResumeClinicModal"
      :showfromhelpsection="false"
      :clinicDatafromhs="resumeClinicData"
      @openLoginModal="openLoginModal"
      @openLoginModalWithJobID="openLoginModalWithJobID"
    />
  </div>
</template>

<script>
import LogoutConfirmModal from "@/components/fixedComponents/LogoutConfirmModal.vue";
import ResumeClinicModal from "@/components/modals/ResumeClinicModal.vue";
import { appConfig } from "@/config/appConfig.js";
import { mapGetters } from "vuex";
import { eventBus } from "@/utils/eventBus";
import { getProfileCompletion, getCandInfo } from "@/utils/apiFunctions.js";
export default {
  props: {
    expanded: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      isNavigating: false,
      showLogoutModal: false,
      localExpanded: false,
      lastToggleAt: 0,
      showResumeClinicModal: false,
      resumeClinicData: {},

      navGroup1: [
        {
          label: "Home",
          icon: require("@/assets/NewDashboard/sidebar/home.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activehome.svg"),
          candidatePath: "homepage",
        },
        {
          label: "Resume",
          icon: require("@/assets/NewDashboard/sidebar/resume.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activeresume.svg"),
          open: false,
          forcedCollapsed: false,
          children: [
            {
              label: "Create Resume",
              icon: require("@/assets/NewDashboard/sidebar/createresume.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activecreateresume.svg"),
              candidatePath: "dashboard",
              mode: "create",
            },
            {
              label: "Fix my resume",
              icon: require("@/assets/NewDashboard/sidebar/fixresume.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activefixresume.svg"),
              openResumeClinic: true,
            },
          ],
        },
        {
          label: "Skill Tests",
          icon: require("@/assets/NewDashboard/sidebar/skilltest.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activeskilltest.svg"),
          candidatePath: "assessment/marketplace",
        },
        {
          label: "Practice Interviews",
          icon: require("@/assets/NewDashboard/sidebar/practiceinterview.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activepracticeinterview.svg"),
          candidatePath: "interview",
        },
        // {
        //   label: "Get Verified",
        //   icon: require("@/assets/NewDashboard/sidebar/getverified.svg"),
        //   iconActive: require("@/assets/NewDashboard/sidebar/activegetverified.svg"),
        //   badge: "NEW",
        // },
        {
          label: "Learn & Upskill",
          icon: require("@/assets/NewDashboard/sidebar/learnupskill.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activelearnupskill.svg"),
          open: false,
          forcedCollapsed: false,
          children: [
            {
              label: "Marketplace",
              icon: require("@/assets/NewDashboard/sidebar/marketplace.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activemarketplace.svg"),
              routerName: "MarketPlace",
              activeFor: [
                "FilteredCoursesView",
                "CourseDetail",
                "CourseView",
                "Review",
                "Instructor",
                "Instructors",
              ],
            },
            {
              label: "Dashboard",
              icon: require("@/assets/NewDashboard/sidebar/tdashboard.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activetdashboard.svg"),
              routerName: "Dashboard",
            },
            {
              label: "Community",
              icon: require("@/assets/NewDashboard/sidebar/community.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activecommunity.svg"),
              routerName: "CommunityView",
            },
            {
              label: "Certificates",
              icon: require("@/assets/NewDashboard/sidebar/certificates.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activecertificate.svg"),
              routerName: "Certificate",
            },
          ],
        },
        {
          label: "Find Jobs",
          icon: require("@/assets/NewDashboard/sidebar/jobs.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activejobs.svg"),
          open: false,
          forcedCollapsed: false,
          children: [
            {
              label: "Job Search",
              icon: require("@/assets/NewDashboard/sidebar/findjobs.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activefindjobs.svg"),
              candidatePath: "job/search",
              jobMode: "search",
            },
            {
              label: "Applied Jobs",
              icon: require("@/assets/NewDashboard/sidebar/applyjobs.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activeapplyjobs.svg"),
              candidatePath: "job/progress",
              jobMode: "applied",
            },
            {
              label: "Saved Jobs",
              icon: require("@/assets/NewDashboard/sidebar/savedjobs.svg"),
              iconActive: require("@/assets/NewDashboard/sidebar/activesavejobs.svg"),
              candidatePath: "job/search?bookmarked=true",
              jobMode: "saved",
            },
          ],
        },
      ],
      navGroup2: [
        {
          label: "My Documents",
          icon: require("@/assets/NewDashboard/sidebar/mydocs.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activemydocs.svg"),
          candidatePath: "documents",
        },
        {
          label: "Account Settings",
          icon: require("@/assets/NewDashboard/sidebar/accountsettings.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/activeaccountsetting.svg"),
          candidatePath: "profile",
        },
        {
          label: "Logout",
          icon: require("@/assets/NewDashboard/sidebar/logout.svg"),
          iconActive: require("@/assets/NewDashboard/sidebar/logout.svg"),
          logout: true,
        },
      ],
    };
  },
  components: {
    LogoutConfirmModal,
    ResumeClinicModal,
  },
  async created() {
    // Load candidate details from the user-parser API (can/user-parser/) using
    // the profile id stored in the session token (same approach as commonForm.js),
    // then refresh the profile-completion value from the progress API. Both are
    // cached in the existing userVUEX store so other parts of the app can reuse them.
    try {
      const response = await getCandInfo();
      const info = response?.status === 200 ? response.data?.data : null;
      if (info) {
        this.$store.commit("userVUEX/setUserInfo", info);
        const p =
          typeof info.profileProgress === "number"
            ? info.profileProgress
            : typeof info.progress === "number"
            ? info.progress
            : null;
        if (typeof p === "number") {
          this.$store.commit("userVUEX/setUserProfileProgress", p);
        }
      }
    } catch (error) {
      console.error(error);
    }
    this.fetchProfileProgress();
  },
  computed: {
    ...mapGetters("userVUEX", {
      candidateInfo: "getUserInfo",
      profileProgress: "getUserProfileProgress",
    }),
    // Profile completion percentage, cached in the userVUEX store
    profileCompletion() {
      const p = this.profileProgress;
      if (typeof p === "number") return p;
      const c = this.candidateInfo || {};
      if (typeof c.progress === "number") return c.progress;
      if (typeof c.profileProgress === "number") return c.profileProgress;
      return 0;
    },
    // Name, image and designation of the person, fetched from the user-parser API
    user() {
      const c = this.candidateInfo || {};
      const name =
        c.fullName ||
        (c.firstName && c.lastName
          ? `${c.firstName} ${c.lastName}`
          : c.firstName || c.lastName || c.name || "Candidate");
      return {
        name,
        role: c.designation || c.experienceLevelFormatted || c.role || " ",
        avatar: c.image || c.profileImage || c.avatar || "",
      };
    },
    showCompleteProfile() {
      return this.profileCompletion < 70;
    },
    sidebarSections() {
      return [
        { items: this.navGroup1, divider: false },
        { items: this.navGroup2, divider: true },
      ];
    },
    isExpanded() {
      return this.expanded !== null ? this.expanded : this.localExpanded;
    },
  },

  methods: {
    goToResumeBuilder() {
      this.switchToCandidate("dashboard");
    },
    async fetchProfileProgress() {
      try {
        const candId = this.candidateInfo?._id || "";
        const res = await getProfileCompletion(candId);
        const p = res?.status === 200 ? res.data?.data?.progress : undefined;
        if (typeof p === "number") {
          this.$store.commit("userVUEX/setUserProfileProgress", p);
        }
      } catch (error) {
        console.error(error);
      }
    },
    confirmLogout() {
      localStorage.clear();
      this.$store.dispatch("userVUEX/LogOut");
      this.allProfileList = [];
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    closeInactiveCollapses() {
      // A mouse-leave can spuriously fire while a toggle click is still being
      // processed (layout shift on open). Don't immediately undo a click.
      if (Date.now() - this.lastToggleAt < 250) return;
      // Close every collapse whose children aren't actively navigated, so a
      // group opened on hover reverts to closed when you leave.
      [this.navGroup1, this.navGroup2].forEach((group) => {
        group.forEach((item) => {
          if (item.children && !this.hasActiveChild(item)) {
            item.open = false;
            item.forcedCollapsed = false;
          }
        });
      });
    },
    handleMouseLeave() {
      if (this.isNavigating) return;
      this.localExpanded = false;
      this.closeInactiveCollapses();
    },
    toggleCollapse(item) {
      this.lastToggleAt = Date.now();
      // Deterministic toggle based purely on the open flag so a re-click always
      // flips state (a parent with an active child can still be collapsed).
      if (item.open) {
        item.open = false;
        item.forcedCollapsed = true;
      } else {
        item.open = true;
        item.forcedCollapsed = false;
      }
    },
    isSubmenuVisible(item) {
      const pinnedOpen = item.open || this.hasActiveChild(item);
      return pinnedOpen && !item.forcedCollapsed;
    },
    handleChildNav(parent, child) {
      // Accordion: when navigating to a child, only the parent that owns it
      // stays open — close every other parent so the previously-active one
      // collapses when you move to a child of a different parent.
      this.navGroup1.forEach((item) => {
        if (item.children) {
          item.open = item === parent;
          item.forcedCollapsed = false;
        }
      });
      this.handleNav(child);
    },
    isActive(item) {
      const name = this.$route && this.$route.name;

      if (item.children) {
        return item.children.some((c) => this.isActive(c));
      }

      if (name && name === item.routerName) return true;

      if (name && item.activeFor && item.activeFor.includes(name)) return true;
      return false;
    },
    hasActiveChild(item) {
      return !!(item.children && item.children.some((c) => this.isActive(c)));
    },
    activeIcon(item) {
      // Only the route-based active item uses the active icon;
      // hovered items keep their normal icon.
      return this.isActive(item);
    },
    handleNav(item) {
      if (item.children) return;

      if (item.logout) {
        this.logout();
        return;
      }

      // "Fix my resume" opens the Resume Clinic modal directly in this app
      // instead of redirecting to the candidate base.
      if (item.openResumeClinic) {
        this.$emit("navigate");
        this.openResumeClinicModal();
        return;
      }

      // Candidate-base items redirect externally, mirroring how NewSidebar's
      // switchToCandidate() navigates out to the candidate app.
      if (item.candidatePath) {
        this.$emit("navigate");
        this.switchToCandidate(item.candidatePath);
        return;
      }

      // Training-app items navigate with the in-app router (NewSidebar's pushPage()).
      if (item.routerName) {
        this.$emit("navigate");
        this.$router.push({ name: item.routerName }).catch(() => {});
        return;
      }

      // Items without a target (e.g. Get Verified / Practice Interviews / Account
      // Settings) are intentionally non-navigating until a path is known.
    },
    switchToCandidate(path) {
      this.isNavigating = true;
      this.$emit("navigating");
      const queryPrefix = path.includes("?") ? "&" : "?";
      window.open(
        `${appConfig.candidateBase}/${path}${queryPrefix}from=candidate`,
        "_self",
      );
    },
    openResumeClinicModal() {
      // Hand over any existing resume-clinic analysis already cached in the
      // userVUEX store; otherwise the modal opens its upload screen and loads
      // analysis from candidateVUEX itself.
      const existing = this.candidateInfo?.resume_clinic_analysis;
      this.resumeClinicData =
        existing && Object.keys(existing).length ? existing : {};
      this.showResumeClinicModal = true;
    },
    closeResumeClinicModal() {
      this.showResumeClinicModal = false;
    },
    logout() {
      this.showLogoutModal = true;
    },
  },
};
</script>

<style scoped>
.mlp-sidebar-inactive {
  padding: 11px 14px;
  height: 41px;
  color: #1e293b;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s;
  justify-content: center;
}
.mlp-sidebar-inactive:hover {
  background: #f3f3f3;
}
.mlp-sidebar-inactive.expanded {
  justify-content: flex-start;
}
.mlp-sidebar-active {
  background: rgba(83, 163, 253, 0.16) !important;
  color: #1d4ed8 !important;
  border-radius: 10px;
}
.mlp-logout {
  color: #ff0000 !important;
}
/* Collapsed state styling fixes: immediately hide submenus and extra labels when not expanded to prevent squishing during collapse transition */
.mlp-root:not(.expanded) .mlp-submenu,
.mlp-root:not(.expanded) .mlp-label,
.mlp-root:not(.expanded) .sidebar-badge,
.mlp-root:not(.expanded) .mlp-caret,
.mlp-root:not(.expanded) .topcard-info,
.mlp-root:not(.expanded) .complete-banner {
  display: none !important;
}

/* Collapsed + active: square background only behind the icon */
.mlp-root:not(.expanded) .mlp-sidebar-inactive.mlp-sidebar-active,
.mlp-root:not(.expanded) .mlp-collapse-toggle.mlp-sidebar-active {
  width: 35px;
  height: 35px;
  margin: 0 auto;
  padding: 0;
  border-radius: 8px;
  justify-content: center;
}
.mlp-label {
  white-space: nowrap;
  min-width: 0;
}
.sidebar-icon + .mlp-label {
  margin-left: 12px;
}
.expanded .mlp-label {
  margin-left: 12px;
}
.mlp-root {
  width: 64px;
  transition: width 0.18s ease;
  /* overflow: hidden; */
}
.mlp-root.expanded {
  width: 240px;
}
.mlp-topcard {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.mlp-root.expanded .mlp-topcard {
  align-items: stretch;
}
.mlp-root:not(.expanded) .mlp-topcard {
  background: transparent;
  border: none;
  box-shadow: none;
  margin-bottom: 0;
}
.mlp-root:not(.expanded) .topcard-img-shadow {
  display: none;
}
.mlp-root:not(.expanded) .topcard-header {
  padding: 0 0 6px 0;
  justify-content: center;
}
.sidebar-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}
.submenu-icon {
  width: 16px;
  height: 16px;
}
.mlp-sidebar-collapse {
  display: flex;
  flex-direction: column;
}
.mlp-collapse-toggle {
  width: 100%;
}
.mlp-caret {
  width: 10px;
  height: 10px;
  margin-left: auto;
  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.6;
}
.mlp-submenu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 24px;
  padding-top: 2px;
  position: relative;
}
.mlp-submenu::before {
  content: "";
  position: absolute;
  left: 20px;
  top: 2px;
  bottom: 2px;
  border: 2px solid #e2e8f0;
  background: #e2e8f0;
  border-radius: 12px;
}
.mlp-submenu-item {
  padding: 8px 12px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  color: #1e293b;
  margin: 2px 5px;
  position: relative;
  white-space: nowrap;
}
.mlp-submenu-item:hover {
  background: #f3f3f3;
}
/* Fill the connector segment for the active child */
.mlp-submenu-item.mlp-sidebar-active::before {
  content: "";
  position: absolute;
  left: -9px;
  top: -2px;
  bottom: 0px;
  border: 2px solid #4a7eff;
  border-radius: 12px;
  background: #4a7eff;
  z-index: 2;
}
.mlp-fade-enter-active,
.mlp-fade-leave-active {
  transition: opacity 0.2s ease, max-height 0.25s ease, transform 0.25s ease;
  overflow: hidden;
  max-height: 220px;
  transform-origin: top;
}
.mlp-fade-enter,
.mlp-fade-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-6px);
}
.mlp-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.sidebar-badge {
  margin-left: auto;
  padding: 2.5px 7px;
  background: #e2e8f0;
  border-radius: 5px;
  font-family: "Inter", sans-serif;
  font-style: normal;
  font-weight: 400;
  height: 19px;
  font-size: 10.5px;
  color: #475569;
}
.sidebar-divider {
  width: 100%;
  height: 2px;
  border: none;
  margin: 10px 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='2'%3E%3Crect x='0' y='0' width='6' height='2' rx='1' fill='%23efefef'/%3E%3C/svg%3E");
  background-repeat: repeat-x;
  background-size: 9px 2px;
}
.mlp-root:not(.expanded) .sidebar-divider {
  width: 28px;
  margin: 10px auto;
}
.mlp-topcard {
  background: linear-gradient(
    132.74deg,
    rgba(255, 255, 255, 0.1) -8.24%,
    rgba(121, 133, 164, 0.1) 74.01%
  );
  border: 1px solid rgba(38, 50, 56, 0.1);
  border-radius: 10px;
  overflow: hidden;
  padding: 0;
  position: relative;
  margin-bottom: 12px;
}
.topcard-img-shadow {
  position: absolute;
  top: 0;
  left: 0;
  height: 70px;
  width: 70px;
  opacity: 0.7;
}
.topcard-header,
.topcard-info,
.company-badge-container {
  padding: 12px 16px 0 16px;
}
/* Avatar + ring */
.avatar-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-ring {
  position: absolute;
  top: 0;
  left: 0;
  transform: rotate(-90deg);
  transform-origin: center;
}

.ring-bg {
  fill: none;
  stroke: #d1d3d6;
  stroke-width: 4;
}

.ring-fill {
  fill: none;
  stroke: #047857;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.6s ease;
}

.avatar-img {
  width: 32px;
  height: 33px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 1;
}

.topcard-arrow {
  cursor: pointer;
}

.completion-badge {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  padding: 0px 4px;
  width: 36px;
  height: 14.5px;
  top: 31px;
  background: #047857;
  border-radius: 5.98316px;
  z-index: 2;
  white-space: nowrap;
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  color: #ffffff;
  text-align: center;
}

.user-name {
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  color: #1f1f1f;
}

.user-role {
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  color: #1f1f1f;
}

/* Company badge */
.company-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  background: #f7f9fb;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #4a7eff;
  width: fit-content;
  border-radius: 20px;
}

/* Complete profile banner */
.complete-banner {
  padding: 0 5.64478px;
  height: 19px;
  background: #e74341;
  font-family: "Inter", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  color: #ffffff;
}
.user-name {
  width: 100%;
  min-width: 0;
  margin: 0;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  white-space: normal !important;
  word-break: break-all;
  overflow-wrap: normal;

  line-height: 22px;
  max-height: 44px;
}
@media (max-width: 1028px) {
  .mlp-root.expanded {
    width: 250px;
  }
}
</style>
